import * as React from "react";
import { Sparkles } from "lucide-react";
import { Modal } from "../../../ui/components/Modal";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Text } from "../../../ui/components/Text";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useI18n } from "../../lib/i18n";
import {
  aiFillTranslations,
  type LocalizationContext,
} from "../../lib/api/localization";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { matchAiFillToEntries, type AiFillEmptyField } from "./aiFillAssist";
import * as s from "./AiFillAssistModal.style.css";

export type AiFillAssistModalProps = {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  sourceLanguage: string;
  sourceLanguageName?: string;
  targetLanguage: string;
  targetLanguageName?: string;
  emptyFields: AiFillEmptyField[];
  /** Apply matched entryId → translated text into the draft. */
  onApply: (updates: Map<string, string>) => void;
};

type ContextGroup = { context: LocalizationContext; fields: AiFillEmptyField[] };
type FailedContext = { context: LocalizationContext; message: string };
type RunResult = { filled: number; requested: number; failed: FailedContext[] };

function groupByContext(fields: AiFillEmptyField[]): ContextGroup[] {
  const order: LocalizationContext[] = [];
  const byContext = new Map<LocalizationContext, AiFillEmptyField[]>();
  for (const field of fields) {
    if (!byContext.has(field.context)) {
      byContext.set(field.context, []);
      order.push(field.context);
    }
    byContext.get(field.context)!.push(field);
  }
  return order.map((context) => ({ context, fields: byContext.get(context)! }));
}

export function AiFillAssistModal({
  open,
  onClose,
  accessToken,
  sourceLanguage,
  sourceLanguageName,
  targetLanguage,
  targetLanguageName,
  emptyFields,
  onApply,
}: AiFillAssistModalProps) {
  const { t } = useI18n();
  const [step, setStep] = React.useState<"options" | "running" | "done">("options");
  const [progress, setProgress] = React.useState<{
    index: number;
    total: number;
    context: string;
  } | null>(null);
  const [result, setResult] = React.useState<RunResult | null>(null);

  const groups = React.useMemo(() => groupByContext(emptyFields), [emptyFields]);

  React.useEffect(() => {
    if (!open) return;
    setStep("options");
    setProgress(null);
    setResult(null);
  }, [open]);

  const runAiFill = async () => {
    setStep("running");

    const merged = new Map<string, string>();
    const failed: FailedContext[] = [];
    let filled = 0;

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      setProgress({ index: i + 1, total: groups.length, context: group.context });
      try {
        const response = await aiFillTranslations(accessToken, {
          targetLanguage,
          sourceLanguage,
          context: group.context,
          items: group.fields.map(({ key, sourceText }) => ({ key, sourceText })),
        });
        const matched = matchAiFillToEntries(group.fields, response.items);
        for (const [entryId, text] of matched) merged.set(entryId, text);
        filled += response.items.length;
      } catch (err) {
        const isProviderNotConfigured =
          err instanceof ProApiClientError &&
          err.status === 503 &&
          err.data?.code === "provider_not_configured";
        const message = isProviderNotConfigured
          ? t("pro.system.localization.aiFill.error.providerNotConfigured")
          : getApiErrorMessage(err, t("pro.system.localization.aiFill.error.generic"));
        failed.push({ context: group.context, message });
        if (isProviderNotConfigured) break;
      }
    }

    if (merged.size > 0) onApply(merged);
    setResult({ filled, requested: emptyFields.length, failed });
    setStep("done");
  };

  return (
    <Modal
      open={open}
      setOpen={(next) => {
        if (!next) onClose();
      }}
      minWidth="480px"
      maxWidth="560px"
      minHeight="auto"
    >
      <Modal.Header>
        <Text level="h3" weight="bold" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={18} aria-hidden />
          {t("pro.system.localization.aiFill.title")}
        </Text>
      </Modal.Header>
      <Modal.Body>
        {step === "options" && (
          <>
            <p className={s.intro}>
              {t("pro.system.localization.aiFill.intro", {
                count: emptyFields.length,
                source: sourceLanguageName ?? sourceLanguage,
                target: targetLanguageName ?? targetLanguage,
              })}
            </p>
            {groups.map((group) => (
              <div key={group.context} className={s.summaryRow}>
                <span className={s.summaryContext}>{group.context}</span>
                <span>
                  {t("pro.system.localization.aiFill.options.summaryLine", {
                    count: group.fields.length,
                    context: group.context,
                  })}
                </span>
              </div>
            ))}
          </>
        )}

        {step === "running" && progress && (
          <AuthTransitionScreen
            variant="compact"
            message={t("pro.system.localization.aiFill.progress", {
              context: progress.context,
              current: progress.index,
              total: progress.total,
            })}
          />
        )}

        {step === "done" && result && (
          <>
            <p className={s.intro}>
              {t("pro.system.localization.aiFill.result.summary", {
                filled: result.filled,
                total: result.requested,
              })}
            </p>
            {result.failed.length > 0 && (
              <Callout variant="error">
                <div>{t("pro.system.localization.aiFill.result.partialFailure")}</div>
                {result.failed.map((f) => (
                  <div key={f.context} className={s.failedContextRow}>
                    {f.context}: {f.message}
                  </div>
                ))}
              </Callout>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === "options" && (
          <>
            <Button type="button" color="neutral" variant="outline" onClick={onClose}>
              {t("shared.common.cancel")}
            </Button>
            <Button
              type="button"
              color="primary"
              variant="solid"
              disabled={groups.length === 0}
              iconLeft={<Sparkles size={14} />}
              onClick={() => void runAiFill()}
            >
              {t("pro.system.localization.aiFill.apply")}
            </Button>
          </>
        )}
        {step === "running" && (
          <Button type="button" color="neutral" variant="outline" disabled>
            {t("shared.common.cancel")}
          </Button>
        )}
        {step === "done" && (
          <Button type="button" color="primary" variant="solid" onClick={onClose}>
            {t("shared.common.close")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
